import { useState } from 'react';

import { Book } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { SelectChronologyModal } from './kim-add-to-chronology';
import { ReferencesDict, SubsetFact } from '../../../../../../../../../../../../backend/src/types/types';
import { useCreateChronologyFromThread } from '@/api/mutations/useCreateChronologyFromThread';
import { FactDropdown } from '@/new/components/fact/fact-accordion';
import { Icon } from '@/new/components/icon';
import { NewChip } from '@/new/components/new-chip';
import { Accordion } from '@/new/components/shadcn/accordion';
import { Button } from '@/new/components/shadcn/button';
import { ButtonGroup } from '@/new/components/shadcn/button-group';
import { useStore } from '@/store';
import { ConfidenceLevelObject, Fact } from '@/types';

export interface SupportingEvidenceProps {
  confidenceLevel: ConfidenceLevelObject;
  facts: SubsetFact[];
  references: ReferencesDict;
  docIds: string[];
}

export function SupportingFacts({ confidenceLevel, facts, references, docIds }: SupportingEvidenceProps) {
  const hasNoContent = facts.length === 0;
  const { matterId, subsetId, threadId } = useParams();

  if (hasNoContent || !matterId || !threadId || !subsetId) {
    return null;
  }

  if (facts.length === 0) {
    return null;
  }

  const { openReferenceDrawer } = useStore();

  const referencesByFactId = references
    ? Object.fromEntries(Object.entries(references).map(([key, value]) => [value.id, key]))
    : null;

  const navigate = useNavigate();
  const { mutate: createChronologyFromThread, isLoading: isCreatingChronology } = useCreateChronologyFromThread();
  const [selectChronsModalOpen, setSelectChronsModalOpen] = useState(false);

  return (
    <div className="mt-4">
      <div className="mx-2 mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Book className="text-brandSecondary mb-0.5 size-4.5" />

          <h2 className="text-sm font-semibold text-gray-800">
            {confidenceLevel.text === 'Low' ? 'Possible Information' : 'Supporting Evidence'}
          </h2>
        </div>

        <div className="flex gap-2">
          <Button size="sm" asChild>
            <Link to={`/matters/${matterId}/subsets/new?documentIds=${docIds.join(',')}`}>
              <Icon className="lucide--group" />
              Create Subset from References
              <NewChip />
            </Link>
          </Button>
          <ButtonGroup>
            <Button
              size="sm"
              onClick={() =>
                createChronologyFromThread(
                  { caseId: subsetId, threadId },
                  {
                    onSuccess: (data) => {
                      navigate(`/matters/${matterId}/subsets/${subsetId}/chronologies/${data.chronologyId}`);
                    },
                  },
                )
              }
              disabled={isCreatingChronology}
            >
              <Icon className="lucide--git-fork" />
              Create Chronology
            </Button>

            <Button size="sm" onClick={() => setSelectChronsModalOpen(true)}>
              <Icon className="lucide--plus" />
              Add to Chronology
            </Button>
          </ButtonGroup>

          <SelectChronologyModal
            isOpen={selectChronsModalOpen}
            handleClose={() => setSelectChronsModalOpen(false)}
            factIds={facts.map((f) => f.id)}
          />
        </div>
      </div>

      <Accordion type="multiple" className="flex flex-col overflow-auto">
        {facts.map((fact) => {
          return (
            <FactDropdown
              key={fact.id}
              fact={fact}
              subsetId={subsetId}
              reference={parseInt(referencesByFactId?.[fact.id] ?? '0')}
              onViewDocument={() => openReferenceDrawer({ factId: fact.id, caseId: subsetId })}
            />
          );
        })}
      </Accordion>
    </div>
  );
}
